import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Users } from 'lucide-react';
import { toast } from 'sonner';
import { studentsAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import {
  Avatar, Badge, DataTable, EmptyPanel, FilterPills, PersonCell, SearchBox, TableRow, adminCard, fmtDate,
} from '../../components/adminUi';

// Course editor · Students tab (s155, admin only): who is enrolled in THIS
// course and how far each one is. Pills filter by progress state, the search
// matches name / email / membership code. A row opens the student's detail.
const COLS = [
  { label: 'Student', width: 'minmax(0,2.2fr)' },
  { label: 'Membership', width: '120px' },
  { label: 'Progress', width: '200px' },
  { label: 'State', width: '120px' },
  { label: 'Last activity', width: '120px' },
  { label: '', width: '24px' },
];

export const STATE = {
  not_started: ['neutral', 'Not started'],
  in_progress: ['info', 'In progress'],
  completed: ['ok', 'Completed'],
};

export function stateBadge(state, enrollmentStatus) {
  if (enrollmentStatus === 'pending') return <Badge kind="warn">{t('Pending')}</Badge>;
  const [kind, label] = STATE[state] || STATE.not_started;
  return <Badge kind={kind}>{t(label)}</Badge>;
}

export function ProgressBar({ pct, done, total }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-center gap-2.5">
        <div
          className="h-1.5 flex-1 overflow-hidden rounded-full bg-lms-line"
          role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
        >
          <div className="h-full bg-lms-accent" style={{ width: `${pct}%` }} />
        </div>
        <span className="w-9 text-right text-[13px] font-semibold text-lms-ink lms-num">{pct}%</span>
      </div>
      {typeof total === 'number' && (
        <span className="text-xs text-lms-sub lms-num">{done}/{total} {t('lessons')}</span>
      )}
    </div>
  );
}

export default function CourseStudents({ courseId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [state, setState] = useState('');

  useEffect(() => {
    const h = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(h);
  }, [search]);

  useEffect(() => {
    studentsAPI.list(courseId, { search: q, progress: state })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error(t('Could not load students')));
  }, [courseId, q, state]);

  const href = (s) => `/admin/courses/${courseId}/students/${s.mid}`;
  const counts = (data && data.counts) || {};

  if (data && data.total === 0) {
    return (
      <EmptyPanel icon={Users} title={t('No students in this course yet')}>
        {t('When a member enrolls or their registration is approved, they will appear here with their progress.')}
      </EmptyPanel>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <FilterPills
        value={state}
        onChange={setState}
        items={[
          { key: '', label: t('All'), count: data ? data.total : undefined },
          { key: 'not_started', label: t('Not started'), count: counts.not_started || 0 },
          { key: 'in_progress', label: t('In progress'), count: counts.in_progress || 0 },
          { key: 'completed', label: t('Completed'), count: counts.completed || 0 },
        ]}
      >
        <SearchBox value={search} onChange={setSearch} placeholder={t('Search name or email…')} className="w-full sm:w-72" />
      </FilterPills>

      {!data ? (
        <p className="text-sm text-lms-muted">{t('Loading…')}</p>
      ) : data.students.length === 0 ? (
        <EmptyPanel icon={Users} title={t('No students match these filters.')} />
      ) : (
        <>
          {/* Desktop / tablet: grid table */}
          <div className="hidden md:block">
            <DataTable cols={COLS}>
              {data.students.map((s) => (
                <TableRow key={s.mid} cols={COLS} onClick={() => navigate(href(s))}>
                  <Link to={href(s)} onClick={(e) => e.stopPropagation()} className="min-w-0 rounded-lg focus-visible:outline focus-visible:outline-2">
                    <PersonCell name={s.name} email={s.email} />
                  </Link>
                  <span className="truncate text-[13px] font-semibold text-lms-ink lms-num">{s.membership_code || '—'}</span>
                  <ProgressBar pct={s.pct} done={s.done} total={s.total} />
                  {stateBadge(s.state, s.enrollment_status)}
                  <span className="text-[13px] text-lms-sub">{fmtDate(s.last_activity)}</span>
                  <ChevronRight size={16} className="text-lms-faint" />
                </TableRow>
              ))}
            </DataTable>
          </div>

          {/* Phone: one card per student */}
          <ul className="flex flex-col gap-3 md:hidden">
            {data.students.map((s) => (
              <li key={s.mid}>
                <Link to={href(s)} className={`${adminCard} lms-press flex flex-col gap-3 p-4 hover:border-lms-ink`}>
                  <div className="flex items-center gap-3">
                    <Avatar name={s.name} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-bold text-lms-ink">{s.name || '—'}</div>
                      <div className="truncate text-[12.5px] text-lms-sub">{s.email}</div>
                    </div>
                    <ChevronRight size={18} className="shrink-0 text-lms-faint" />
                  </div>
                  <ProgressBar pct={s.pct} done={s.done} total={s.total} />
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-lms-sub">
                    {stateBadge(s.state, s.enrollment_status)}
                    {s.membership_code && <span className="font-semibold text-lms-ink lms-num">{s.membership_code}</span>}
                    <span>{t('Last activity')}: {fmtDate(s.last_activity)}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
